'use client'
import { FC, useEffect, useMemo, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { useSession } from '@/hooks'
import type { TypeCmsProduct, TypeCmsVariant } from '@/schema'
import { mq, vw } from '@/styles'
import { DetailHeader } from './detail/DetailHeader'
import { SectionNutritionChart } from './detail/SectionNutritionChart'
import { SectionIngredients } from './detail/SectionIngredients'
import { SectionMoreInfo } from './detail/SectionMoreInfo'

/**
 * data: PDP res data
 * variant: variant data from grid/slider
 */
type TypeMainProduct = { data: { product: TypeCmsProduct; variant: TypeCmsVariant }; variant?: TypeCmsVariant | null }

const DynamicSectionRelated = dynamic(() => import('./detail/SectionRelateds').then(mod => mod.SectionRelated))

export const getVariantByQueryParam = (data, variantName) => {
  const variant = data?.variants?.find(item => item.name === variantName)
  return variant ? variant : (data?.variants?.[0] ?? null)
}

const getInitialVariant = (data, variant, isPDP, queryVariant) => {
  if (data?.variant) return data.variant
  if (variant) return variant
  if (!isPDP) return data?.product?.variants?.[0] || null
  if (queryVariant) return getVariantByQueryParam(data, queryVariant)
  return data?.product?.variants?.[0] || null
}

export const SectionProduct: FC<TypeMainProduct> = ({ data = null, variant = null }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const queryVariant = searchParams.get('v') || null
  const isPDP = pathname.includes('/product/')
  const productData = data?.product || null
	const { user } = useSession()

  const [currentVariant, setCurrentVariant] = useState(getInitialVariant(data, variant, isPDP, queryVariant))

  const {
    id,
    bigcommerceId,
    name,
    brand,
    slug,
    filters,
    description,
    nutrition,
    ingredients,
    infoSpecifications,
    variants,
    sku,
    categories,
    related,
  } = productData || {}

  const header = useMemo(
    () => ({ id, bigcommerceId, name, brand, filters, description, variants, sku, slug, categories }),
    [bigcommerceId, brand, categories, description, filters, id, name, sku, slug, variants]
  )

  useEffect(() => {
    if (!productData) return

    let nextVariant = null
    if (queryVariant) {
      nextVariant = getVariantByQueryParam(productData, queryVariant)
    } else if (currentVariant?.variant_id) {
      nextVariant = productData.variants.find(v => v.bigcommerceId === currentVariant?.variant_id)
    } else {
      nextVariant = productData.variants?.[0]
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentVariant(nextVariant || null)
  }, [currentVariant?.variant_id, productData, queryVariant])

  return (
		<ProductSection>
			{currentVariant && <DetailHeader
				data={header}
				currentVariant={currentVariant}
				setCurrentVariant={setCurrentVariant}
				isPack={false}
				isPrivate={!!user && !!user.id}
			/>}
			{nutrition?.length > 0 && <SectionNutritionChart data={nutrition} />}
			{!!ingredients && <SectionIngredients data={ingredients} />}
			{infoSpecifications?.length > 0 && <SectionMoreInfo data={infoSpecifications} />}
			{related?.length > 0 && <DynamicSectionRelated data={related} />}
		</ProductSection>
  )
}

const ProductSection = styled.section`
  background-color: var(--color-white);
  grid-column: 1 / span 6;
  grid-row: unset;
  margin: 0 auto 200px;
  max-width: 100%;
  padding: ${vw(20, 'mobile')} 0;
  width: 90vw;

  ${mq.greaterThan('nexus7')} {
    grid-column: 1 / span 6;
    margin: 0 auto 200px;
    padding: ${vw(20, 'tablet')} 0;
  }

  ${mq.greaterThan('tablet')} {
    border-radius: 20px;
    grid-column: 4 / span 9;
    grid-row: 1 / span 4;
    margin: 0 auto 200px;
    padding: ${vw(30, 'desktop')} 0;
    width: ${vw(580, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin: 0 auto 200px;
    padding: 30px 0;
    width: 580px;
  }

  header,
  section {
    padding: 0 16px;

    &:last-child {
      margin-bottom: 120px;
    }
  }

  .product-drawer-body & {
    border-radius: 0;
    width: 100%;
  }
`
